import i18n from '@/common/i18n';
import { VhsTaskStatus } from '@schema';
import type { ElNotificationOptions } from '@uikit/ui-kit/types/notification';
import type { VhsTaskResponse } from '@schema';
import computedLocale from '@/common/utils/computedLocale';

export type VeOperationType = 'create' | 'start' | 'stop' | 'delete' | 'reboot' | 'migrate';
export type VnOperationType = 'create' | 'start' | 'stop' | 'delete';
export type VhsResourceType = 've' | 'vn';
export type VhsOperationType<T extends VhsResourceType = VhsResourceType> =
  T extends 've' ? VeOperationType :
  T extends 'vn' ? VnOperationType :
  never;

export type VhsNotificationHandler = (task: VhsTaskResponse) => Partial<ElNotificationOptions> | false | undefined;

export type VhsTaskMap = {
  [K in VhsResourceType]?: {
    [K2 in VhsOperationType<K>]?: {
      [K3 in VhsTaskStatus]?: VhsNotificationHandler
    }
  }
};

type VhsNotificationContext = {
  action: string;
  actionVerb: string;
  actionVerbInProgress: string;
  resource: string;
  resourceName: string;
  message: string;
}

const RESOURCE_TYPE_LABELS = computedLocale<Record<VhsResourceType, string>>(() => ({
  ve: i18n.t('Virtual environment'),
  vn: i18n.t('Virtual network'),
}));

const ACTION_LABELS = computedLocale(() => ({
  ve: {
    create: i18n.t('creation'),
    start: i18n.t('start'),
    stop: i18n.t('stop'),
    delete: i18n.t('deletion'),
    reboot: i18n.t('reboot'),
    migrate: i18n.t('migration'),
  },
  vn: {
    create: i18n.t('creation'),
    start: i18n.t('start'),
    stop: i18n.t('stop'),
    delete: i18n.t('deletion'),
  },
}));

const ACTION_VERBS = computedLocale(() => ({
  ve: {
    create: i18n.t('created'),
    start: i18n.t('started'),
    stop: i18n.t('stopped'),
    delete: i18n.t('deleted'),
    reboot: i18n.t('rebooted'),
    migrate: i18n.t('migrated'),
  },
  vn: {
    create: i18n.t('created'),
    start: i18n.t('started'),
    stop: i18n.t('stopped'),
    delete: i18n.t('deleted'),
  },
}));

const ACTION_VERBS_IN_PROGRESS = computedLocale(() => ({
  ve: {
    create: i18n.t('creating'),
    start: i18n.t('starting'),
    stop: i18n.t('stopping'),
    delete: i18n.t('deleting'),
    reboot: i18n.t('rebooting'),
    migrate: i18n.t('migrating'),
  },
  vn: {
    create: i18n.t('creating'),
    start: i18n.t('starting'),
    stop: i18n.t('stopping'),
    delete: i18n.t('deleting'),
  },
}));

const buildNotificationContext = (task: VhsTaskResponse): VhsNotificationContext => {
  const operationType = task.operation_type as VhsOperationType;
  const resourceType = task.resource_type as VhsResourceType;
  const resourceId = task.resource_id;

  return {
    action: ACTION_LABELS[resourceType][operationType],
    actionVerb: ACTION_VERBS[resourceType][operationType],
    actionVerbInProgress: ACTION_VERBS_IN_PROGRESS[resourceType][operationType],
    resource: RESOURCE_TYPE_LABELS[resourceType],
    resourceName: resourceId,
    message: task.message ?? '',
  };
};

const createNotificationHandler = (
  notification: (context: VhsNotificationContext) => Partial<ElNotificationOptions>
) => (task: VhsTaskResponse) => {
  const context = buildNotificationContext(task);
  return notification(context);
};

const createOperationHandlers = () => ({
  [VhsTaskStatus.COMPLETED]: createNotificationHandler(ctx => ({
    title: i18n.t('VHS operation {operationType} completed', { operationType: ctx.action }),
    message: i18n.t('{resource} "{resourceName}" successfully {actionVerb}', {
      resource: ctx.resource,
      resourceName: ctx.resourceName,
      actionVerb: ctx.actionVerb,
    }),
  })),
  [VhsTaskStatus.PENDING]: createNotificationHandler(ctx => ({
    title: i18n.t('VHS operation {operationType} pending', { operationType: ctx.action }),
    message: i18n.t('{resource} "{resourceName}" {action} pending', {
      resource: ctx.resource,
      resourceName: ctx.resourceName,
      action: ctx.action,
    }),
  })),
  [VhsTaskStatus.RUNNING]: createNotificationHandler(ctx => ({
    title: i18n.t('VHS operation {operationType} in progress', { operationType: ctx.action }),
    message: i18n.t('{actionVerbInProgress} {resource} "{resourceName}"...', {
      actionVerbInProgress: ctx.actionVerbInProgress,
      resource: ctx.resource,
      resourceName: ctx.resourceName,
    }),
  })),
  [VhsTaskStatus.CANCELLED]: createNotificationHandler(ctx => ({
    title: i18n.t('VHS operation {operationType} cancelled', { operationType: ctx.action }),
    message: i18n.t('{resource} "{resourceName}" {action} canceled', {
      resource: ctx.resource,
      resourceName: ctx.resourceName,
      action: ctx.action,
    }),
  })),
  [VhsTaskStatus.FAILED]: createNotificationHandler(ctx => ({
    title: i18n.t('VHS operation {operationType} failed', { operationType: ctx.action }),
    message: ctx.message,
  })),
});

export const VHS_TASK_MAP: VhsTaskMap = computedLocale<VhsTaskMap>(() => ({
  ve: {
    create: createOperationHandlers(),
    start: createOperationHandlers(),
    stop: createOperationHandlers(),
    delete: createOperationHandlers(),
    reboot: createOperationHandlers(),
    migrate: createOperationHandlers(),
  },
  vn: {
    create: createOperationHandlers(),
    start: createOperationHandlers(),
    delete: createOperationHandlers(),
    migrate: createOperationHandlers(),
  },
}));

export const getVhsNotification = (task: VhsTaskResponse): Partial<ElNotificationOptions> | false | undefined => {
  const { operation_type: operationType, status } = task;

  const handler = VHS_TASK_MAP[task.resource_type as VhsResourceType]?.[operationType as VhsOperationType]?.[status];

  return handler?.(task) ?? false;
};
